import * as React from "react";
import { Box, Button, Card, Stack, Typography } from "@mui/material";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { FileDashedIcon } from "@phosphor-icons/react/dist/ssr/FileDashed";
import { LockIcon } from "@phosphor-icons/react/dist/ssr/Lock";
import { useNavigate } from "react-router-dom";

/**
 * What a campaign-request screen shows when there is no request behind the id in the address.
 *
 * This replaces a bare red alert that was a dead end in the literal sense: it said "This
 * request could not be loaded" and then stopped, with no link, no button and no nav — so a
 * stale bookmark, a shared link to a deleted draft, or a mistyped path left the client staring
 * at an error with nothing to click but the browser's back button. An error state that offers
 * no next step is a worse failure than the one it is reporting.
 *
 * It is deliberately not an `<Alert>`. Alerts are for something that went wrong *inside* a
 * working screen; this is the whole screen, and the thing it most needs to do is get the
 * person somewhere useful. So it is built like the list's own empty state — same card, same
 * centred stack, same weight of copy — because "there is nothing here" is what both are
 * saying, and a client should not have to work out that one of them is a failure.
 *
 * Two shapes, because there are two reasons the screen can be empty and they need different
 * words. `missing` is the ordinary one: the request is gone, or never existed. `forbidden` is
 * the tenant boundary answering — a request is read through its own tenant connection, so it
 * is only visible from the workspace that owns it, and telling someone to switch workspace is
 * useful where telling them the request does not exist would be a lie.
 */
export interface RequestNotFoundProps {
	/** Which of the two stories to tell. Defaults to the ordinary one. */
	variant?: "missing" | "forbidden";
	/** Overrides the default heading, for a screen with a more specific thing to say. */
	title?: string;
	/**
	 * What the API said, where it said anything useful. Shown under the explanation rather than
	 * instead of it — a raw "Request not found" is a fact, not an answer.
	 */
	detail?: string | null;
	/** Where the way out goes. */
	backTo: string;
	backLabel: string;
}

export function RequestNotFound({
	variant = "missing",
	title,
	detail,
	backTo,
	backLabel,
}: RequestNotFoundProps): React.JSX.Element {
	const navigate = useNavigate();
	const forbidden = variant === "forbidden";

	const Icon = forbidden ? LockIcon : FileDashedIcon;
	const heading = title ?? (forbidden ? "This request belongs to another workspace" : "We can’t find that request");

	return (
		<Box
			sx={{
				maxWidth: "var(--Content-maxWidth)",
				m: "var(--Content-margin)",
				p: "var(--Content-padding)",
				width: "var(--Content-width)",
			}}
		>
			<Card variant="outlined" sx={{ p: 6, textAlign: "center" }}>
				<Stack spacing={2} sx={{ alignItems: "center" }}>
					{/*
					 * `text.secondary` rather than `error.main`: the tone the screen is going for is
					 * "nothing here", not "something broke". A red page for a link that has simply
					 * aged out reads as an incident to the person who clicked it.
					 */}
					<Icon size={40} color="var(--mui-palette-text-secondary)" weight="duotone" />

					<Box>
						<Typography variant="h6">{heading}</Typography>

						<Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mt: 0.5 }}>
							{forbidden
								? "A request is only visible from the workspace that owns it. Switch to that client’s account and open it again — the queue names the client on every row."
								: "It may have been deleted, or the link may be out of date. Nothing has been lost from the requests that are still open."}
						</Typography>

						{detail ? (
							<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
								{detail}
							</Typography>
						) : null}
					</Box>

					<Button startIcon={<ArrowLeftIcon />} variant="contained" onClick={() => navigate(backTo)}>
						{backLabel}
					</Button>
				</Stack>
			</Card>
		</Box>
	);
}
